import { useContext, useState } from "react";
import ThemeContext from "../../lib/theme-context";
import CalendarDayCell from "./CalendarDayCell";
import moment from "moment";

const CalendarGrid = ({
  currentMonth,
  events,
  viewMode,
  onEventClick,
  onEventMove,
  onMonthChange,
}) => {
  const theme = useContext(ThemeContext);
  const isDark = theme[0] === "dark";
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);

  const handleDragStart = (e, event) => {
    const isDraggable =
      (event.category === "forecast" || event.category === "reflection") &&
      event.category !== "holiday";

    if (!isDraggable) {
      e.preventDefault();
      return;
    }

    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target);
  };

  const handleDragOver = (day) => {
    setDragOverDate(day.format("YYYY-MM-DD"));
  };

  const handleDragLeave = () => {
    setDragOverDate(null);
  };

  const handleDrop = async (day) => {
    if (!draggedEvent) return;

    const newDate = day.toDate();
    const isSameDate = moment(draggedEvent.date).isSame(day, "day");

    if (!isSameDate && onEventMove) {
      await onEventMove(draggedEvent, newDate);
    }

    setDraggedEvent(null);
    setDragOverDate(null);
  };

  const startOfMonth = moment(currentMonth).startOf("month");
  const endOfMonth = moment(currentMonth).endOf("month");
  const startDay = startOfMonth.clone().startOf("week").add(1, "day");
  const endDay = endOfMonth.clone().endOf("week").add(1, "day");

  const calendar = [];
  let currentDay = startDay.clone();

  if (viewMode === "week") {
    const startOfWeek = moment(currentMonth).startOf("week").add(1, "day");
    const week = [];
    for (let i = 0; i < 7; i++) {
      week.push(startOfWeek.clone().add(i, "days"));
    }
    calendar.push(week);
  } else {
    while (currentDay.isBefore(endDay, "day")) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(currentDay.clone());
        currentDay.add(1, "day");
      }
      calendar.push(week);
    }
  }

  const eventsByDate = {};
  events.forEach((event) => {
    const dateKey = event.date;
    if (!eventsByDate[dateKey]) {
      eventsByDate[dateKey] = [];
    }
    eventsByDate[dateKey].push(event);
  });

  return (
    <div
      style={{
        padding: "clamp(8px, 2vw, 16px)",
        backgroundColor: isDark ? "#1a1b1e" : "#f9fafb",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", gap: "clamp(4px, 1vw, 8px)", alignItems: "center" }}>
          <button
            onClick={() => {
              const newMonth = moment(currentMonth).subtract(
                viewMode === "week" ? 1 : 1,
                viewMode === "week" ? "week" : "month"
              );
              onMonthChange(newMonth);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#3d3e43" : "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#2d2e33" : "#e5e7eb";
            }}
            style={{
              padding: "6px 12px",
              backgroundColor: isDark ? "#2d2e33" : "#e5e7eb",
              color: isDark ? "#e5e7eb" : "#374151",
              border: "2px solid transparent",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "12px",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            Previous
          </button>
          <button
            onClick={() => {
              onMonthChange(moment());
            }}
            onMouseEnter={(e) => {
              if (moment().isSame(currentMonth, "month")) {
                e.currentTarget.style.transform = "scale(1.02)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(55, 136, 216, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "none";
            }}
            style={{
              padding: "6px 12px",
              backgroundColor: moment().isSame(currentMonth, "month")
                ? isDark ? "rgba(55, 136, 216, 0.2)" : "rgba(55, 136, 216, 0.15)"
                : isDark ? "#2d2e33" : "#e5e7eb",
              color: moment().isSame(currentMonth, "month") ? "#3788d8" : isDark ? "#e5e7eb" : "#374151",
              border: "2px solid transparent",
              borderColor: moment().isSame(currentMonth, "month") ? "#3788d8" : "transparent",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "12px",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            Today
          </button>
          <button
            onClick={() => {
              const newMonth = moment(currentMonth).add(
                viewMode === "week" ? 1 : 1,
                viewMode === "week" ? "week" : "month"
              );
              onMonthChange(newMonth);
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#3d3e43" : "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? "#2d2e33" : "#e5e7eb";
            }}
            style={{
              padding: "6px 12px",
              backgroundColor: isDark ? "#2d2e33" : "#e5e7eb",
              color: isDark ? "#e5e7eb" : "#374151",
              border: "2px solid transparent",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "12px",
              transition: "all 0.2s ease",
              whiteSpace: "nowrap",
            }}
          >
            Next
          </button>
        </div>

        <div
          style={{
            fontSize: "clamp(16px, 3vw, 20px)",
            fontWeight: "600",
            color: isDark ? "#e5e7eb" : "#374151",
            textAlign: "right",
          }}
        >
          {viewMode === "week"
            ? `Week of ${moment(currentMonth).startOf("week").add(1, "day").format("MMM D, YYYY")}`
            : currentMonth.format("MMMM YYYY")}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "clamp(2px, 0.5vw, 4px)",
          marginBottom: "4px",
        }}
      >
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, index) => (
          <div
            key={day}
            style={{
              padding: "clamp(4px, 1vw, 8px)",
              textAlign: "center",
              fontWeight: "600",
              fontSize: "clamp(10px, 2vw, 13px)",
              color: isDark ? "#9ca3af" : "#6b7280",
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {calendar.map((week, weekIndex) => (
        <div
          key={weekIndex}
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "clamp(2px, 0.5vw, 4px)",
            marginBottom: "clamp(2px, 0.5vw, 4px)",
          }}
        >
          {week.map((day, dayIndex) => {
            const dateKey = day.format("YYYY-MM-DD");
            const dayEvents = eventsByDate[dateKey] || [];
            const isToday = day.isSame(moment(), "day");
            const isCurrentMonth = day.isSame(currentMonth, "month");
            const isDragOver = dragOverDate === dateKey;

            return (
              <CalendarDayCell
                key={dayIndex}
                day={day}
                events={dayEvents}
                isToday={isToday}
                isCurrentMonth={isCurrentMonth}
                onEventClick={onEventClick}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                isDragOver={isDragOver}
                viewMode={viewMode}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default CalendarGrid;
